<template>
    <Transition v-if="isLoading"
                name="spinner">
        <div id="cover-spin">
            <span class="loader">
                <span>{{ $spinnerText }}</span>
            </span>
        </div>
    </Transition>

    <v-form :name="props.formProps.formName">
        <Tabs value="0"
              scrollable>

            <TabList class="mb-4">
                <Tab value="0">{{ __('Basic data') }}</Tab>
                <Tab value="1">{{ __('Progress') }}</Tab>
                <Tab value="2">{{ __('Changelog') }}</Tab>
            </TabList>

            <TabPanels>
                <TabPanel value="0">
                    <div class="grid">
                        <FormInputText fieldName="title"
                                       label="Title"
                                       size="8"
                                       validation="required"
                                       :isFormDisabled="isFormDisabled"
                                       :isFocus="true"
                                       v-model:field="crud.title" />

                        <FormSelect v-if="formProps.addMode !== true"
                                    fieldName="status_id"
                                    label="Status"
                                    size="4"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.statuses"
                                    v-model:field="crud.status_id" />
                        <FormStaticText v-else
                                        size="4" />

                    </div>
                    <div class="grid">
                        <FormSelect fieldName="user_id"
                                    label="Assigned user"
                                    size="3"
                                    validation="required"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.users"
                                    v-model:field="crud.user_id" />

                        <FormSelect fieldName="creator_id"
                                    label="Creator"
                                    size="3"
                                    validation="required"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.users"
                                    v-model:field="crud.creator_id" />


                        <FormSelect fieldName="dctpriority_id"
                                    label="Priority"
                                    size="2"
                                    validation="required"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.dct_priority_tasks"
                                    v-model:field="crud.dctpriority_id" />

                        <FormCalendar fieldName="creation_date"
                                      label="Creation date"
                                      size="2"
                                      validation="required"
                                      showTime
                                      hourFormat="24"
                                      dateFormat="yy-mm-dd HH:mm"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.creation_date" />

                        <FormCalendar fieldName="due_date"
                                      label="Due date"
                                      size="2"
                                      validation="required"
                                      showTime
                                      hourFormat="24"
                                      dateFormat="yy-mm-dd HH:mm"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.due_date" />
                    </div>
                    <div class="grid">
                        <FormEditor fieldName="description"
                                    label="Description"
                                    :isFormDisabled="isFormDisabled"
                                    v-model:field="crud.description" />
                    </div>
                </TabPanel>

                <TabPanel value="1">
                    <div class="grid">
                        <FormInputNumber fieldName="progress"
                                         label="Progress (%)"
                                         size="4"
                                         :minFractionDigits="0"
                                         :maxFractionDigits="2"
                                         :min="0"
                                         :max="100"
                                         suffix="%"
                                         :isFormDisabled="isFormDisabled"
                                         v-model:field="crud.progress" />

                        <FormInputNumber fieldName="workload"
                                         label="Workload (hours)"
                                         size="4"
                                         :minFractionDigits="0"
                                         :maxFractionDigits="2"
                                         :min="0"
                                         suffix="h"
                                         :isFormDisabled="isFormDisabled"
                                         v-model:field="crud.workload" />

                        <FormCalendar fieldName="completion_date"
                                      label="Completion date"
                                      size="4"
                                      showTime
                                      hourFormat="24"
                                      dateFormat="yy-mm-dd HH:mm"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.completion_date" />
                    </div>
                </TabPanel>

                <TabPanel v-if="crud.log"
                          value="2"
                          :pt="{ root: { class: 'form-tab-panel' } }">
                    <ChangeLog :log="crud?.log" />
                </TabPanel>
            </TabPanels>
        </Tabs>

        <div class="grid">
            <FormButtons :formProps="props.formProps"
                         :sourceForm="crud"
                         :validation="validation"
                         v-model:isFormDisabled="isFormDisabled">
                <template #after-in-edit>
                    <AlertAddButton :addAlert="addAlert" />
                </template>
            </FormButtons>
        </div>
        <AlertAdd :formProps="{ source_table: 'tasks', source_id: crud.id, width: '50%' }"
                  v-model:isAddAlertDialogOpen="isAddAlertDialogOpen" />
    </v-form>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';

import { validation, initForm } from '@pages/Helper/crud_utils.js'
import FormInputText from '@crud/FormInputText.vue'
import FormInputNumber from '@crud/FormInputNumber.vue'
import FormSelect from '@crud/FormSelect.vue'
import FormStaticText from '@crud/FormStaticText.vue'
import FormCalendar from '@crud/FormCalendar.vue'
import FormEditor from '@crud/FormEditor.vue'
import FormButtons from '@crud/FormButtons.vue'
import ChangeLog from '@crud/ChangeLog.vue'
import AlertAdd from '@app/Data/Alert/AlertAdd.vue'
import AlertAddButton from '@alert/AlertAddButton.vue'
import CustomButton from '@crud/CustomButton.vue'
import { __ } from '@helper/utils.js'

// -----------------------------------------------------

const props = defineProps({
    formProps: Object,
})

const isLoading = ref(true);
const isFormDisabled = ref(true)
const crud = ref({})

const options = ["statuses", "users", "dct_priority_tasks"];
const select = ref(null);
const isAddAlertDialogOpen = ref(false);

// -----------------------------------------------------

onMounted(async () => {
    await initForm(props, crud, isLoading, options, select)
})

// -----------------------------------------------------

const addAlert = async () => {
    isAddAlertDialogOpen.value = true;
}

// -----------------------------------------------------

</script>

<style lang="scss" scoped></style>
